
import { Component, Vue } from 'vue-property-decorator'
import {
  auditAppleNotifications,
  AppleNotificationAuditResult
} from '@/api/appleNotifications'

@Component({ name: 'AppleNotifications' })
export default class extends Vue {
  private loading = false
  private result: AppleNotificationAuditResult | null = null
  private hourOptions = [1, 6, 12, 24, 48, 72, 168]

  // No environment here on purpose. App Store Connect delivers each
  // environment's notifications to the server configured for it, so this
  // server can only audit its own; the backend decides which that is and
  // reports it back. Offering the choice once produced a false alarm - 32
  // sandbox notifications the test server had handled correctly were shown as
  // missing because the audit ran against production.
  private query: { hours: number } = { hours: 6 }

  private async runAudit(replay: boolean) {
    this.loading = true
    try {
      const res = await auditAppleNotifications({
        hours: this.query.hours,
        replay
      })
      // The request wrapper resolves with the whole axios response and does not
      // unwrap the API envelope, so the payload is one level deeper than it
      // looks: response.data is { status, data }.
      const data: AppleNotificationAuditResult = res.data.data
      this.result = data
      if (replay) {
        this.$message.success(this.$t('appleNotif.replayedN', { n: data.summary.replayed }) as string)
      } else if (data.summary.missing > 0) {
        this.$message.warning(this.$t('appleNotif.missingN', { n: data.summary.missing }) as string)
      } else {
        this.$message.success(this.$t('appleNotif.noneMissing') as string)
      }
    } catch (err: any) {
      this.$message.error(this.errorMessage(err))
    } finally {
      this.loading = false
    }
  }

  private confirmReplay() {
    const count = this.result ? this.result.summary.missing : 0
    this.$confirm(
      this.$t('appleNotif.replayConfirmBody', { n: count }) as string,
      this.$t('appleNotif.replayConfirmTitle') as string,
      {
        confirmButtonText: this.$t('appleNotif.continue') as string,
        cancelButtonText: this.$t('appleNotif.cancel') as string,
        type: 'warning'
      }
    )
      .then(() => this.runAudit(true))
      .catch(() => undefined)
  }

  private rowClass({ row }: any) {
    return row.processed ? '' : 'row-missing'
  }

  private replayTagType(outcome?: string) {
    if (outcome === 'replayed') return 'success'
    if (outcome === 'no_action_needed') return 'info'
    return 'danger'
  }

  private replayLabel(outcome?: string) {
    const known = ['replayed', 'no_action_needed', 'handler_error', 'failed']
    if (outcome && known.includes(outcome)) {
      return this.$t('appleNotif.outcome_' + outcome) as string
    }
    return outcome || '-'
  }

  // The backend answers this one with a code the operator can act on: the
  // audit needs Apple Store Server API credentials, and without them every
  // window - production or sandbox alike - fails the same way.
  private errorMessage(err: any) {
    const raw = err?.response?.data?.status?.message || err?.message || ''
    if (/not configured/i.test(raw)) {
      return this.$t('appleNotif.errorNotConfigured') as string
    }
    return raw || (this.$t('appleNotif.errorGeneric') as string)
  }

  private formatDate(value?: string | null) {
    if (!value) return '-'
    const d = new Date(value)
    return isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR')
  }
}
